import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const chapters = [
    {
        number: "01",
        title: "Friction & Process Audit",
        description:
            "We dissect your existing deal flows, CRM bottlenecks and conversion leakages. You get a brutally honest map of where revenue is slipping away.",
    },
    {
        number: "02",
        title: "Architecture & Automation",
        description:
            "We build custom multi-channel outreach, CRM syncing and automated follow-up triggers — engineered around how your buyers actually buy.",
    },
    {
        number: "03",
        title: "AI Copilot Integration",
        description:
            "We train AI copilots on your best reps' closing frameworks, so every lead gets an instant, on-brand, high-converting response.",
    },
    {
        number: "04",
        title: "Continuous Velocity Scaling",
        description:
            "Real-time dashboard tracking, A/B testing of sales scripts and aggressive revenue scaling. Your engine gets faster every single month.",
    },
];

export default function HowItWorks() {
    return (
        <section id="process" data-testid="how-it-works-section" className="px-4 sm:px-8 lg:px-16 py-24 lg:py-36 bg-[#0E121B]/50">
            <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: EASE }}
                            className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-semibold mb-4"
                        >
                            The SalesCan Method
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                            className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-5xl text-white leading-tight"
                        >
                            Four chapters to
                            <br />
                            <span className="text-stroke">sales velocity.</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                            className="mt-6 text-base text-slate-400 leading-relaxed max-w-md"
                        >
                            No fluff, no 90-slide decks. A disciplined operating system that
                            turns your sales process into a compounding asset.
                        </motion.p>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {chapters.map((c, i) => (
                        <motion.div
                            key={c.number}
                            data-testid={`process-chapter-${c.number}`}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.85, delay: i * 0.08, ease: EASE }}
                            className="group border-t border-white/10 last:border-b py-10 flex gap-6 sm:gap-10 items-start hover:bg-white/[0.02] transition-colors duration-500 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-lg"
                        >
                            <span className="font-mono-tech text-4xl sm:text-5xl font-semibold text-stroke-red group-hover:text-[#FF3B30] group-hover:[-webkit-text-stroke:0px] transition-all duration-500 shrink-0">
                                {c.number}
                            </span>
                            <div>
                                <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl">
                                    {c.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
